import { useStore } from "@/stores/useStore.ts";
import { useCallback, useEffect, useState } from "react";
import { SearchOptions } from "@/models";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";

export const usePaginatedData = <T>(
  search?: (
    tenantId: string,
    searchString: string,
    page: number,
    perPage: number,
    orderBy?: SortingState
  ) => Promise<{ total: number; data: T[]; count: number }>
) => {
  const tenant = useStore((s) => s.tenant);
  const [searchParams, setSearchParams] = useSearchParams();
  const querySearchString = searchParams.get("search") || "";
  const queryPageIndex = searchParams.get("pageIndex") || "0";
  const queryPageSize = searchParams.get("pageSize") || "10";
  const querySorting = searchParams.get("sorting");
  const [data, setData] = useState<T[]>([]);
  const [searchOptions, setSearchOptions] = useState<SearchOptions<T>[]>();
  const [searchString, setSearchString] = useState(querySearchString);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: Number(queryPageIndex),
    pageSize: Number(queryPageSize),
  });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>(
    querySorting
      ? querySorting.split(",").map((sort) => {
          const [id, desc] = sort.split(":");
          return { id, desc: desc === "desc" };
        })
      : []
  );

  useEffect(() => {
    setLoading(true);
    const params = {
      search: searchString,
      pageIndex: pagination.pageIndex.toString(),
      pageSize: pagination.pageSize.toString(),
      sorting: sorting
        .map((sort) => `${sort.id}:${sort.desc ? "desc" : "asc"}`)
        .join(","),
    };

    setSearchParams((prevState) => ({ ...prevState, ...params }));

    if (search && tenant) {
      search(
        tenant.id,
        searchString,
        pagination.pageIndex + 1,
        pagination.pageSize,
        sorting
      ).then((res) => {
        setData(res.data);
        setRowCount(res.total);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [search, tenant, searchString, pagination, sorting, setSearchParams]);

  const handleSearchStringUpdate = useCallback(
    (search: string) => {
      setSearchString(search);
      setPagination((prevState) => ({ ...prevState, pageIndex: 0 }));
    },
    [setSearchString, setPagination]
  );

  return {
    data,
    pagination,
    setPagination,
    rowCount,
    searchOptions,
    setSearchOptions,
    searchString,
    handleSearchStringUpdate,
    loading,
    setLoading,
    sorting,
    setSorting,
  };
};
